import { isWeb } from '@/constants/environment'
import { StorageKeyEnum } from '@/constants/storage'
import { AutomaticContainerRemoval } from '@/features/pos/types'
import { Printer } from '@/features/print/types'
import { NotificationSoundPreferences } from '@/features/settings/types'
import AsyncStorage from '@react-native-async-storage/async-storage'
import * as SecureStore from 'expo-secure-store'

type StorageType = 'async' | 'secure'

const parseAutomaticContainerRemoval = (value: unknown): AutomaticContainerRemoval | undefined => {
    if (!value || typeof value !== 'object') return undefined

    const removal = value as Record<string, unknown>
    const cartItemUuid = removal.cartItemUuid
    const cartItemAddedProductVariantUuid = removal.cartItemAddedProductVariantUuid
    const hasCartItemUuid = typeof cartItemUuid === 'string' && !!cartItemUuid
    const hasAddedProductVariantUuid =
        typeof cartItemAddedProductVariantUuid === 'string' && !!cartItemAddedProductVariantUuid

    if (hasCartItemUuid === hasAddedProductVariantUuid) return undefined

    return hasCartItemUuid
        ? { cartItemUuid }
        : { cartItemAddedProductVariantUuid: cartItemAddedProductVariantUuid as string }
}

class StorageService {
    private keyStorageMap: Record<StorageKeyEnum, StorageType> = {
        [StorageKeyEnum.LicenceId]: 'async',
        [StorageKeyEnum.DeviceId]: 'async',
        [StorageKeyEnum.CurrentProfileUuid]: 'async',
        [StorageKeyEnum.DeviceToken]: isWeb ? 'async' : 'secure',
        [StorageKeyEnum.PosCartKey]: 'async',
        [StorageKeyEnum.PhoneCartKey]: 'async',
        [StorageKeyEnum.PosAutomaticContainerRemovals]: 'async',
        [StorageKeyEnum.PhoneAutomaticContainerRemovals]: 'async',
        [StorageKeyEnum.Printers]: 'async',
        [StorageKeyEnum.RefreshToken]: isWeb ? 'async' : 'secure',
        [StorageKeyEnum.NotificationSoundPreferences]: 'async',
    }

    private async setItem(key: StorageKeyEnum, value: string): Promise<void> {
        const storageType = this.keyStorageMap[key]

        if (storageType === 'async') {
            await AsyncStorage.setItem(key, value)
        } else if (storageType === 'secure') {
            await SecureStore.setItemAsync(key, value)
        } else {
            throw new Error(`Unknown storage type for key: ${key}`)
        }
    }

    private async getItem(key: StorageKeyEnum): Promise<string | null> {
        const storageType = this.keyStorageMap[key]

        if (storageType === 'async') {
            return await AsyncStorage.getItem(key)
        } else if (storageType === 'secure') {
            return await SecureStore.getItemAsync(key)
        } else {
            throw new Error(`Unknown storage type for key: ${key}`)
        }
    }

    private async removeItem(key: StorageKeyEnum): Promise<void> {
        const storageType = this.keyStorageMap[key]

        if (storageType === 'async') {
            await AsyncStorage.removeItem(key)
        } else if (storageType === 'secure') {
            await SecureStore.deleteItemAsync(key)
        } else {
            throw new Error(`Unknown storage type for key: ${key}`)
        }
    }

    async setLicenceId(licenceId: string) {
        await this.setItem(StorageKeyEnum.LicenceId, licenceId)
    }

    async getLicenceId() {
        return await this.getItem(StorageKeyEnum.LicenceId)
    }

    async removeLicenceId() {
        await this.removeItem(StorageKeyEnum.LicenceId)
    }

    async setDeviceId(deviceId: string) {
        await this.setItem(StorageKeyEnum.DeviceId, deviceId)
    }

    async getDeviceId() {
        return await this.getItem(StorageKeyEnum.DeviceId)
    }

    async removeDeviceId() {
        await this.removeItem(StorageKeyEnum.DeviceId)
    }

    async setDeviceToken(deviceToken: string) {
        await this.setItem(StorageKeyEnum.DeviceToken, deviceToken)
    }

    async getDeviceToken() {
        return await this.getItem(StorageKeyEnum.DeviceToken)
    }

    async removeDeviceToken() {
        await this.removeItem(StorageKeyEnum.DeviceToken)
    }

    async setCurrentProfileUuid(uuid: string) {
        await this.setItem(StorageKeyEnum.CurrentProfileUuid, uuid)
    }

    async getCurrentProfileUuid() {
        return await this.getItem(StorageKeyEnum.CurrentProfileUuid)
    }

    async removeCurrentProfileUuid() {
        await this.removeItem(StorageKeyEnum.CurrentProfileUuid)
    }

    async setPosCartKey(cartKey: string) {
        await this.setItem(StorageKeyEnum.PosCartKey, cartKey)
    }

    async getPosCartKey() {
        return await this.getItem(StorageKeyEnum.PosCartKey)
    }

    async removePosCartKey() {
        await this.removeItem(StorageKeyEnum.PosCartKey)
    }

    async setPhoneCartKey(cartKey: string) {
        await this.setItem(StorageKeyEnum.PhoneCartKey, cartKey)
    }

    async getPhoneCartKey() {
        return await this.getItem(StorageKeyEnum.PhoneCartKey)
    }

    async removePhoneCartKey() {
        await this.removeItem(StorageKeyEnum.PhoneCartKey)
    }

    async setAutomaticContainerRemovals(
        mode: 'pos' | 'phone',
        cartKey: string,
        removals: AutomaticContainerRemoval[]
    ) {
        const key =
            mode === 'pos'
                ? StorageKeyEnum.PosAutomaticContainerRemovals
                : StorageKeyEnum.PhoneAutomaticContainerRemovals

        await this.setItem(key, JSON.stringify({ cartKey, removals }))
    }

    async getAutomaticContainerRemovals(mode: 'pos' | 'phone', cartKey: string) {
        const key =
            mode === 'pos'
                ? StorageKeyEnum.PosAutomaticContainerRemovals
                : StorageKeyEnum.PhoneAutomaticContainerRemovals
        const value = await this.getItem(key)

        if (!value) return []

        try {
            const stored = JSON.parse(value) as Record<string, unknown>

            if (stored.cartKey !== cartKey || !Array.isArray(stored.removals)) return []

            return stored.removals.flatMap((removal) => {
                const parsed = parseAutomaticContainerRemoval(removal)

                return parsed ? [parsed] : []
            })
        } catch {
            return []
        }
    }

    async removeAutomaticContainerRemovals(mode: 'pos' | 'phone') {
        await this.removeItem(
            mode === 'pos'
                ? StorageKeyEnum.PosAutomaticContainerRemovals
                : StorageKeyEnum.PhoneAutomaticContainerRemovals
        )
    }

    async setPrinters(printers: Printer[]) {
        await this.setItem(StorageKeyEnum.Printers, JSON.stringify(printers))
    }

    async getPrinters(): Promise<Printer[]> {
        const printers = await this.getItem(StorageKeyEnum.Printers)

        return printers ? JSON.parse(printers) : []
    }

    async setRefreshToken(token: string) {
        await this.setItem(StorageKeyEnum.RefreshToken, token)
    }

    async getRefreshToken() {
        return await this.getItem(StorageKeyEnum.RefreshToken)
    }

    async removeByKeys(keys: StorageKeyEnum[]) {
        await Promise.allSettled(keys.map((key) => this.removeItem(key)))
    }

    async setNotificationSoundPreferences(prefs: NotificationSoundPreferences) {
        await this.setItem(StorageKeyEnum.NotificationSoundPreferences, JSON.stringify(prefs))
    }

    async getNotificationSoundPreferences(): Promise<NotificationSoundPreferences | null> {
        const prefs = await this.getItem(StorageKeyEnum.NotificationSoundPreferences)

        return prefs ? JSON.parse(prefs) : null
    }
}

export default new StorageService()
